<template>
  <div class="q-pa-md bg-white">
    <div v-if="error">An error occured</div>
    <div v-else>
      <div>
        <h5 class="text-h6 q-my-sm">Referrer Report</h5>
        <div class="q-mb-md">
            <date-range-selector
                :selectedRange="dateFilter"
                @onApply="refreshData"
            />
        </div>
      </div>
      <q-table
        flat
        bordered
        :data="referrerReport"
        separator="cell"
        :columns="columns"
        :loading="loading"
        :cardContainerStyle="{backgroundColor:'red'}"
        :hide-bottom="true"
        :rows-per-page-options="[-1]"
      >

        <template v-slot:body="props">
          <q-tr :props="props">
            <q-td key="name" :props="props">{{ props.row.name }}</q-td>
            <q-td key="quoteCount" :props="props">
              <q-btn
                color="primary"
                outline
                padding="xs lg"
                @click="props.row.quoteCount && openFilteredQuotesDialog(props.row.referrerId)"
              >
                {{ props.row.quoteCount }}
              </q-btn>
            </q-td>
            <q-td key="convertedOrderCount" :props="props">
              {{ props.row.convertedOrderCount }}
            </q-td>
            <q-td key="totalOrderCount" :props="props">
              <q-btn
                color="primary"
                outline
                padding="xs lg"
                @click="props.row.totalOrderCount && openFilteredOrdersDialog(props.row.referrerId)"
              >
                {{ props.row.totalOrderCount }}
              </q-btn>
            </q-td>
            <q-td key="orderConversion" :props="props">
              {{ getConvertedOrderPercentage(props.row) }}
            </q-td>
            <q-td key="totalDispatchCount" :props="props">
              <q-btn
                color="primary"
                outline
                padding="xs lg"
                @click="
                props.row.totalDispatchCount &&
                openFilteredDispatchesDialog(props.row.referrerId)"
              >
                {{ props.row.totalDispatchCount }}
              </q-btn>
            </q-td>
            <q-td key="dispatchConversion" :props="props">
              {{getDispatchConversion(props.row) }}
            </q-td>
            <q-td key="leadCost" :props="props">{{ props.row.leadCost | currency }}</q-td>
            <q-td key="orderCost" :props="props">{{ getOrderCost(props.row) }}</q-td>
            <q-td key="totalQuoteCost" :props="props">
              {{ props.row.leadCost * props.row.quoteCount | currency }}
            </q-td>
            <q-td key="orderAmount" :props="props">{{ props.row.orderAmount | currency }}</q-td>
            <q-td key="carrierPayAmount" :props="props">
              {{ props.row.carrierPayAmount | currency }}
            </q-td>
            <q-td key="depositAmount" :props="props">{{ props.row.depositAmount | currency}}</q-td>
            <q-td key="avgDeposit" :props="props">
              {{ getAverageDeposit(props.row)}}
            </q-td>
          </q-tr>
        </template>
        <template v-slot:bottom-row>
          <q-tr>
            <q-td><strong>Total</strong></q-td>
            <q-td><strong>{{totals.quoteCount}}</strong></q-td>
            <q-td><strong>{{totals.convertedOrderCount}}</strong></q-td>
            <q-td>
              <q-btn
                color="primary"
                outline
                padding="xs lg"
                @click="totals.totalOrderCount && openFilteredOrdersDialog()"
              >
                <strong>{{totals.totalOrderCount}}</strong>
              </q-btn>
            </q-td>
            <q-td>
              <strong>
                {{getPercentage(totals.totalOrderCount, totals.quoteCount)}}
              </strong>
            </q-td>
            <q-td><strong>{{totals.totalDispatchCount}}</strong></q-td>
            <q-td>
              <strong>
                {{getPercentage(totals.totalDispatchCount, totals.quoteCount)}}
              </strong>
            </q-td>
            <q-td><strong>{{totals.leadCost | currency}}</strong></q-td>
            <q-td><strong>{{ getOrderCost(totals)}}</strong></q-td>
            <q-td><strong>{{totals.leadCost * totals.quoteCount | currency}}</strong></q-td>
            <q-td><strong>{{totals.orderAmount | currency}}</strong></q-td>
            <q-td><strong>{{totals.carrierPayAmount | currency}}</strong></q-td>
            <q-td><strong>{{totals.depositAmount | currency}}</strong></q-td>
            <q-td>
              <strong>
                {{getAverageDeposit(totals)}}
              </strong>
            </q-td>
          </q-tr>
        </template>
      </q-table>
    </div>

    <filtered-orders
      ref="filteredOrdersDialogRef"
    />

    <filtered-quotes
      ref="filteredQuotesDialogRef"
    />

    <filtered-dispatches
      ref="filteredDispatchesDialogRef"
    />
  </div>
</template>

<script>
  import graphql from '@client/graphql';
  import DateRangeSelector from '@client/components/date-range-selector';
  import { thisMonthDateRange } from '@client/utils/app-helpers';
  import FilteredOrders from '../filtered-orders/filtered-orders';
  import FilteredQuotes from '../filtered-orders/filtered-quotes';
  import FilteredDispatches from '../filtered-orders/filtered-dispatches';

  const tcolumns = [
      {
      name: 'name',
      label: 'Referrer',
      align: 'center'
      },
      {
      name: 'quoteCount',
      label: '# of Quotes',
      align: 'center'
      },
      {
        name: 'convertedOrderCount',
        label: '# of Converted Quotes',
        align: 'center'
      },
      {
      name: 'totalOrderCount',
      label: '# of Orders',
      align: 'center'
      },
      {
      name: 'orderConversion',
      label: 'Conversion Percentage',
      align: 'center'
      },

      {
      name: 'totalDispatchCount',
      label: 'Dispatched',
      align: 'center'
      },
      {
      name: 'dispatchConversion',
      label: 'Dispatch Conversion',
      align: 'center'
      },
      {
      name: 'leadCost',
      label: 'Quote Cost',
      align: 'center'
      },
      {
      name: 'orderCost',
      label: 'Order Cost',
      align: 'center'
      },
      {
      name: 'totalQuoteCost',
      label: 'Total Quote Cost',
      align: 'center'
      },
      {
      name: 'orderAmount',
      label: 'Order Amount',
      align: 'center'
      },
      {
      name: 'carrierPayAmount',
      label: 'Carrier Pay Amount',
      align: 'center'
      },
      {
      name: 'depositAmount',
      label: 'Amount to Broker',
      align: 'center'
      },
      {
      name: 'avgDeposit',
      label: 'Average Deposit',
      align: 'center'
      }
  ];
  export default {
    components: {
      DateRangeSelector,
      FilteredOrders,
      FilteredQuotes,
      FilteredDispatches
    },
    apollo: {
      currentUser: {
        query: graphql.queries.currentUser
      },
      referrerReport: {
        query: graphql.queries.getReferrerReport,
        variables() {
            return {
                startDate: this.dateFilter.from,
                endDate: this.dateFilter.to
            };
        },
        watchLoading(isLoading) {
          this.loading = isLoading;
        },
        error(error) {
          this.error = error;
        },
        result(result) {
          if (result.loading) return;
          this.referrerReport = result.data.referrerReport;
          this.calculateTotals(this.referrerReport);
        }
      }
    },
    data() {
      return {
        loading: false,
        error: null,
        referralReport: [],
        dateFilter: thisMonthDateRange,
        totals: [],
        columns: tcolumns
      };
    },

    methods: {
      refreshData(dateRange) {
        this.dateFilter = { ...dateRange };
      },
      getConvertedOrderPercentage(row) {
        return this.getPercentage(row.totalOrderCount, row?.quoteCount);
      },
      getDispatchConversion(row) {
          return this.getPercentage(row.totalDispatchCount, row?.quoteCount);
      },
      getAverageDeposit(row) {
        return (row.totalDispatchCount ? this.currencyFilter(Math.round(((row.depositAmount * 100) / row.totalDispatchCount) / 100)) : 'NA');
      },
      getOrderCost(row) {
        return row.totalOrderCount ? this.currencyFilter((row.leadCost * row.quoteCount) / row.totalOrderCount) : 'NA';
      },
      calculateTotals(data) {
        const cols = [
          'quoteCount',
          'convertedOrderCount',
          'totalDispatchCount',
          'totalOrderCount',
          'leadCost',
          'orderAmount',
          'carrierPayAmount',
          'depositAmount'
        ];
        cols.forEach((name) => {
          this.totals[name] = (data && data.length) ?
                              data.map((dt) => dt[name]).reduce((a, b) => a + b) : 0;
        });
      },
      openFilteredOrdersDialog(referrerId) {
        this.$refs.filteredOrdersDialogRef.openDialog({
          dateRangeStart: this.dateFilter.from,
          dateRangeEnd: this.dateFilter.to,
          referrerId
        });
      },
      openFilteredQuotesDialog(referrerId) {
        this.$refs.filteredQuotesDialogRef.openDialog({
          dateRangeStart: this.dateFilter.from,
          dateRangeEnd: this.dateFilter.to,
          referrerId
        });
      },
      openFilteredDispatchesDialog(referrerId) {
        this.$refs.filteredDispatchesDialogRef.openDialog({
          dateRangeStart: this.dateFilter.from,
          dateRangeEnd: this.dateFilter.to,
          referrerId
        });
      }
    }
  };
</script>

<style>
  table {
    text-align: center;
  }
</style>
